import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useThree, useFrame, useLoader } from '@react-three/fiber'
import {
  Group,
  Vector3,
  Vector2,
  Matrix4,
  Euler,
  Quaternion,
  MathUtils,
  Scene,
  Mesh,
  PlaneGeometry,
  OrthographicCamera,
  PerspectiveCamera,
  ShaderMaterial,
  WebGLRenderTarget,
  TextureLoader,
  RepeatWrapping,
  LinearFilter,
  LinearSRGBColorSpace,
  SRGBColorSpace,
  type Material,
} from 'three'

const LEAVES_TEXTURE = '/leaves.webp'

// Empty in the GLTF that marks where the projector sits.
const PROJECTOR_NODE = 'projectorSpotlight'

// Resolution of the offscreen komorebi texture. The pattern is low-frequency so
// this is plenty; it's re-rendered every frame.
const RES = 512

type Shape = 'ellipse' | 'rectangle' | 'full'
const SHAPE_INDEX: Record<Shape, number> = { ellipse: 0, rectangle: 1, full: 2 }

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // PlaneGeometry(2,2) already spans clip space; project straight through.
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`

// Komorebi: dappled light through leaves. We sample a tiled, gently swaying leaf
// texture (leaves.webp), then threshold its luminance so light only passes through
// the gaps. Output is the *shadow* amount (so it can be multiplied onto surfaces):
// mask * (1 - light). Outside the footprint mask is 0, i.e. no darkening.
const fragmentShader = /* glsl */ `
  uniform sampler2D uLeaves;
  uniform float uTime;
  uniform float uDensity;  // tiling of the leaf texture
  uniform vec2  uScale;    // per-axis stretch (e.g. tall = "long shadows")
  uniform float uSpeed;
  uniform float uSway;
  uniform float uCoverage;
  uniform float uSoftClose; // gap softness near the centre (close)
  uniform float uSoftFar;   // gap softness toward the rim (far)
  uniform vec2  uExtent;   // mask half-extents (normalized), larger axis = 1
  uniform float uShape;    // 0 ellipse, 1 rectangle, 2 full
  uniform float uEdge;     // mask edge feather

  varying vec2 vUv;

  void main() {
    vec2 uv = vUv;
    // Per-axis stretch: dividing the sample coords by uScale enlarges features
    // along that axis (uScale.y > 1 → tall, long-shadow look).
    vec2 suv = uv * uDensity / uScale;

    // Bounded sway (no infinite scroll); uSpeed drives its rate.
    float st = uTime * uSpeed;
    vec2 sway = uSway * vec2(sin(st * 0.6 + uv.y * 5.0), cos(st * 0.5 + uv.x * 5.0));

    // Normalized distance from the footprint centre (0 centre, ~1 rim), reused
    // for both the softness falloff and the mask.
    vec2 nd = uv * 2.0 - 1.0;
    float d = (uShape < 0.5)
      ? length(nd / uExtent)
      : max(abs(nd.x) / uExtent.x, abs(nd.y) / uExtent.y);

    // Softness blends from the centre (close) to the rim (far); at d = 0.5 it's
    // exactly halfway between the two.
    float softness = max(mix(uSoftClose, uSoftFar, clamp(d, 0.0, 1.0)), 0.001);

    // A near-binary leaf texture won't soften from a value band alone, so blur
    // the *sample* via a mip-LOD bias scaled by softness — that's what actually
    // feathers the shadow edges. Luminance = light through the gaps.
    vec3 tex = texture2D(uLeaves, suv + sway, softness * 8.0).rgb;
    float n = dot(tex, vec3(0.2126, 0.7152, 0.0722));

    // Light passes through the gaps: higher coverage lowers the threshold; the
    // value band tracks softness too, to clean up the blurred edge.
    float thr = mix(0.72, 0.18, uCoverage);
    float band = max(softness, 0.02);
    float light = smoothstep(thr - band, thr + band, n);

    // Footprint mask (ellipse / rectangle / full).
    float mask = (uShape > 1.5) ? 1.0 : 1.0 - smoothstep(1.0 - uEdge, 1.0, d);

    // Shadow amount: dark under the leaves, zero in the gaps and outside the mask.
    float shadow = (1.0 - light) * mask;
    gl_FragColor = vec4(vec3(shadow), 1.0);
  }
`

// Injected into every scene material to multiply the projected komorebi shadow
// over its output, sampling the texture through the projector's frustum.
const PROJ_HEAD = /* glsl */ `
varying vec3 vProjWorldPos;
uniform mat4 uProjVP;
uniform sampler2D uProjMap;
uniform float uProjStrength;
uniform float uProjThreshold;
`

const PROJ_BODY = /* glsl */ `
{
  vec4 pc = uProjVP * vec4(vProjWorldPos, 1.0);
  if (pc.w > 0.0) {
    vec2 puv = (pc.xy / pc.w) * 0.5 + 0.5;
    if (all(greaterThanEqual(puv, vec2(0.0))) && all(lessThanEqual(puv, vec2(1.0)))) {
      float shadow = texture2D(uProjMap, puv).r;
      float factor = 1.0 - uProjStrength * shadow;
      // Work on luminance, then rescale the colour by the same ratio. This dims
      // only the part *above* the threshold (dark areas untouched, highlights
      // never pushed below it) while preserving hue/saturation — so bright spots
      // get darker instead of washing out to grey.
      vec3 c = gl_FragColor.rgb;
      float L = dot(c, vec3(0.2126, 0.7152, 0.0722));
      float Ltarget = min(L, uProjThreshold) + max(L - uProjThreshold, 0.0) * factor;
      gl_FragColor.rgb = c * (L > 1e-4 ? Ltarget / L : 1.0);
    }
  }
}
`

export interface KomorebiProjector {
  // Patch a scene material so it multiplies the projected komorebi shadow over
  // its output. Call this before the material is first compiled (e.g. from
  // useSceneProcessing) so it's baked into the single compile — no extra recompile.
  patchMaterial: (mat: Material) => void
  // Compile the offscreen komorebi material and render the projection texture
  // once. Await this before showing the scene so the first visible frame isn't
  // stalled by the fbo shader compiling / sampling an empty render target.
  warmUp: (gl: import('three').WebGLRenderer) => Promise<void>
}

// Fixed projector configuration.
//   angleH/V  — footprint half-angles (deg); the larger drives the frustum.
//   rotation  — XYZ Euler (deg) from the projectorSpotlight empty (-Z at zero).
//   strength/threshold — darkening amount, and the brightness above which to dim.
//   density/scale/coverage/softClose/softFar — the leaf look.
//   sway/speed — bounded breeze motion (set either to 0 to freeze + stop rendering).
const SETTINGS = {
  enabled: true,
  strength: 0.5,
  threshold: 0.03,
  angleH: 69,
  angleV: 34,
  shape: 'ellipse' as Shape,
  edgeSoftness: 0.5,
  distance: 20,
  rotation: { x: -16, y: 23, z: 0 },
  density: 6,
  scaleX: 1,
  scaleY: 1,
  coverage: 1,
  softClose: 0.01,
  softFar: 0.34,
  sway: 0.15,
  speed: 0.2,
}

// Renders an animated "light through leaves" pattern offscreen and projects it
// onto the scene as multiplied darkness (no light is added). The hook owns the
// render target, shader and projector camera; materials are wired in by passing
// `patchMaterial` to useSceneProcessing.
export function useKomorebiProjector(scene: Group): KomorebiProjector {
  const maxAnisotropy = useThree((s) => s.gl.capabilities.getMaxAnisotropy())
  const leaves = useLoader(TextureLoader, LEAVES_TEXTURE)

  const ctrl = SETTINGS

  // Offscreen komorebi pipeline: a fullscreen quad with the shader, rendered into
  // a render target whose texture is projected onto the scene as shadow.
  const { rt, fboScene, fboCamera, material, geometry } = useMemo(() => {
    const rt = new WebGLRenderTarget(RES, RES, { depthBuffer: false, stencilBuffer: false })
    rt.texture.minFilter = LinearFilter
    rt.texture.magFilter = LinearFilter
    rt.texture.generateMipmaps = false
    rt.texture.colorSpace = LinearSRGBColorSpace // use the shader output verbatim
    const material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uLeaves: { value: null },
        uTime: { value: 0 },
        uDensity: { value: 6 },
        uScale: { value: new Vector2(1, 1) },
        uSpeed: { value: 0.15 },
        uSway: { value: 0.15 },
        uCoverage: { value: 0.5 },
        uSoftClose: { value: 0.2 },
        uSoftFar: { value: 0.02 },
        uExtent: { value: new Vector2(1, 1) },
        uShape: { value: 0 },
        uEdge: { value: 0.05 },
      },
    })
    const geometry = new PlaneGeometry(2, 2)
    const fboScene = new Scene()
    fboScene.add(new Mesh(geometry, material))
    const fboCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1)
    return { rt, fboScene, fboCamera, material, geometry }
  }, [])

  useEffect(() => {
    rt.texture.anisotropy = maxAnisotropy
  }, [rt, maxAnisotropy])

  // Tile + filter the leaf texture and bind it to the shader.
  useEffect(() => {
    leaves.wrapS = leaves.wrapT = RepeatWrapping
    leaves.colorSpace = SRGBColorSpace
    leaves.anisotropy = maxAnisotropy
    leaves.needsUpdate = true
    material.uniforms.uLeaves.value = leaves
  }, [leaves, material, maxAnisotropy])

  useEffect(() => () => {
    rt.dispose()
    material.dispose()
    geometry.dispose()
  }, [rt, material, geometry])

  // Shared projector uniforms — the same objects are injected into every patched
  // material, so mutating them here updates the whole scene at once.
  const projVP = useMemo(() => ({ value: new Matrix4() }), [])
  const projStrength = useMemo(() => ({ value: 0.5 }), [])
  const projThreshold = useMemo(() => ({ value: 0.5 }), [])
  const projMap = useMemo(() => ({ value: rt.texture }), [rt])

  const patchMaterial = useCallback((mat: Material) => {
    if (!(mat as any).isMeshStandardMaterial || (mat as any).__komorebiPatched) return
    ;(mat as any).__komorebiPatched = true
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uProjVP = projVP
      shader.uniforms.uProjMap = projMap
      shader.uniforms.uProjStrength = projStrength
      shader.uniforms.uProjThreshold = projThreshold
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vProjWorldPos;')
        .replace('#include <project_vertex>', '#include <project_vertex>\nvProjWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;')
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\n${PROJ_HEAD}`)
        .replace('#include <dithering_fragment>', `#include <dithering_fragment>\n${PROJ_BODY}`)
    }
  }, [projVP, projMap, projStrength, projThreshold])

  // World position of the projector empty in the loaded scene.
  const origin = useMemo(() => {
    const node = scene.getObjectByName(PROJECTOR_NODE)
    if (!node) return new Vector3()
    node.updateWorldMatrix(true, false)
    return node.getWorldPosition(new Vector3())
  }, [scene])

  // Orientation from the XYZ Euler rotation: forward aims the frustum (-Z at
  // zero), up rolls the projected pattern.
  const { targetPos, up } = useMemo(() => {
    const q = new Quaternion().setFromEuler(new Euler(
      MathUtils.degToRad(ctrl.rotation.x),
      MathUtils.degToRad(ctrl.rotation.y),
      MathUtils.degToRad(ctrl.rotation.z),
    ))
    const forward = new Vector3(0, 0, -1).applyQuaternion(q)
    const upVec = new Vector3(0, 1, 0).applyQuaternion(q)
    return {
      targetPos: origin.clone().add(forward.multiplyScalar(ctrl.distance)),
      up: upVec,
    }
  }, [origin, ctrl.rotation.x, ctrl.rotation.y, ctrl.rotation.z, ctrl.distance])

  // Frustum half-angle = the larger of H/V; the mask aspect comes from the ratio
  // of their tangents (tan maps angle -> footprint size on the wall).
  const { coneAngle, ex, ey } = useMemo(() => {
    const tanH = Math.tan(MathUtils.degToRad(ctrl.angleH))
    const tanV = Math.tan(MathUtils.degToRad(ctrl.angleV))
    const maxTan = Math.max(tanH, tanV)
    return { coneAngle: Math.atan(maxTan), ex: tanH / maxTan, ey: tanV / maxTan }
  }, [ctrl.angleH, ctrl.angleV])

  // The camera whose view-projection maps world space into the komorebi texture.
  const projCamera = useMemo(() => new PerspectiveCamera(50, 1, 0.05, 100), [])

  // The render target only needs (re)drawing when the texture changes; set this
  // on any look/param change so a static pattern renders once instead of per frame.
  const dirty = useRef(true)

  // Push the look uniforms only when they change.
  useEffect(() => {
    const u = material.uniforms
    u.uDensity.value = ctrl.density
    u.uScale.value.set(ctrl.scaleX, ctrl.scaleY)
    u.uSpeed.value = ctrl.speed
    u.uSway.value = ctrl.sway
    u.uCoverage.value = ctrl.coverage
    u.uSoftClose.value = ctrl.softClose
    u.uSoftFar.value = ctrl.softFar
    u.uExtent.value.set(ex, ey)
    u.uShape.value = SHAPE_INDEX[ctrl.shape as Shape]
    u.uEdge.value = ctrl.edgeSoftness
    dirty.current = true
  }, [material, ex, ey, ctrl.density, ctrl.scaleX, ctrl.scaleY, ctrl.coverage,
    ctrl.softClose, ctrl.softFar, ctrl.sway, ctrl.speed, ctrl.shape, ctrl.edgeSoftness])

  // Darkening params, read by the patched scene materials every frame.
  useEffect(() => {
    projStrength.value = ctrl.enabled ? ctrl.strength : 0
    projThreshold.value = ctrl.threshold
    dirty.current = true
  }, [projStrength, projThreshold, ctrl.enabled, ctrl.strength, ctrl.threshold])

  // Refresh the projector's view-projection only when its orientation changes.
  useEffect(() => {
    projCamera.position.copy(origin)
    projCamera.up.copy(up)
    projCamera.lookAt(targetPos)
    projCamera.fov = MathUtils.radToDeg(coneAngle * 2)
    projCamera.far = ctrl.distance
    projCamera.updateProjectionMatrix()
    projCamera.updateMatrixWorld(true)
    projCamera.matrixWorldInverse.copy(projCamera.matrixWorld).invert()
    projVP.value.multiplyMatrices(projCamera.projectionMatrix, projCamera.matrixWorldInverse)
  }, [projCamera, projVP, origin, up, targetPos, coneAngle, ctrl.distance])

  const renderRT = useCallback((gl: import('three').WebGLRenderer) => {
    const prev = gl.getRenderTarget()
    gl.setRenderTarget(rt)
    gl.render(fboScene, fboCamera)
    gl.setRenderTarget(prev)
  }, [rt, fboScene, fboCamera])

  // Re-render the komorebi texture each frame while it animates, otherwise just
  // once after a change.
  useFrame((state) => {
    if (!ctrl.enabled) return
    if (ctrl.sway > 0 && ctrl.speed > 0) material.uniforms.uTime.value = state.clock.elapsedTime
    else if (!dirty.current) return
    dirty.current = false
    renderRT(state.gl)
  })

  // Precompile the fbo shader and populate the render target once, so the scene's
  // first visible frame doesn't stall on it. Used by useSceneProcessing's
  // readiness handshake.
  const warmUp = useCallback(async (gl: import('three').WebGLRenderer) => {
    await gl.compileAsync(fboScene, fboCamera)
    renderRT(gl)
    dirty.current = false
  }, [fboScene, fboCamera, renderRT])

  return { patchMaterial, warmUp }
}
